import infoIcon from "@assets/actions/info.svg";
import {
  ENABLE_CONSENT_V3_NEWSLETTER,
  NEWSLETTER_MESSAGES,
} from "@config/newsletter";
import { getValidEmailRegex } from "@helper/utils";
import cx from "classnames";
import type { ReactElement, SubmitEvent } from "react";
import { useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import {
  submitViaConsentV3,
  submitViaLegacyEndpoint,
} from "./helpers/newsletterSubmit";
import styles from "./NewsletterSignupForm.module.css";

type Props = Readonly<{
  testId?: string;
}>;

export default function SignupForm({
  testId = "newsletter-form",
}: Props): ReactElement {
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [successMsg, setSuccessMsg] = useState<string>("");
  const formRef = useRef<HTMLFormElement>(null);

  const handleOnSubmit = (e: SubmitEvent<HTMLFormElement>) => {
    void handleSubmitAsync(e);
  };

  const handleSubmitAsync = async (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Guard clause: exit early if the form reference is missing
    if (!formRef.current) return;

    const formData = new FormData(formRef.current);
    const data = Object.fromEntries(formData.entries());
    const email =
      typeof data["enter-email"] === "string" ? data["enter-email"].trim() : "";
    const isMarketingConsent = "consent-email" in data;

    const isEmailValid = email && getValidEmailRegex().test(email);
    if (!isEmailValid) {
      setErrorMsg(NEWSLETTER_MESSAGES.INVALID_EMAIL);
      return;
    }

    // Feature-flagged rollout: use the new Consent v3 API only when enabled,
    // otherwise fall back to the legacy newsletterSignup endpoint.
    const submit = ENABLE_CONSENT_V3_NEWSLETTER
      ? submitViaConsentV3
      : submitViaLegacyEndpoint;
    const result = await submit(email, isMarketingConsent);

    if (result.errorMsg !== undefined) setErrorMsg(result.errorMsg);
    if (result.successMsg !== undefined) setSuccessMsg(result.successMsg);
  };

  return (
    <form
      onSubmit={handleOnSubmit}
      data-testid={testId}
      className="mb-md"
      ref={formRef}
    >
      <div
        className={cx(
          errorMsg || successMsg ? styles.alert : "",
          errorMsg ? styles.alertError : successMsg ? styles.alertSuccess : "",
        )}
      >
        {errorMsg || successMsg}
      </div>
      <div className={styles.formGroup}>
        <input
          id="enter-email"
          name="enter-email"
          type="email"
          className={styles.formControl}
          placeholder="Enter your email address"
        />
      </div>
      <p className={cx(styles.privacy, "w-12/12 pl-0")}>
        Dengan mendaftar, saya menerima{" "}
        <a href="https://sph.com.sg/legal/website_tnc">Terma & Syarat</a> dan{" "}
        <a href="https://www.sph.com.sg/tnc/pdpa/">
          Notis Perlindungan Data Peribadi
        </a>{" "}
        SPH Media, sebagaimana yang dipinda dari semasa ke semasa.
      </p>
      <div className="flex items-center my-4 space-x-2">
        <input
          name="consent-email"
          id="consent-email"
          type="checkbox"
          className="appearance-auto h-3 w-3 text-brand-primary border-brand-primary rounded focus:ring-blue-500"
        />
        <label
          htmlFor="consent-email"
          className="lg:flex gap-1 items-center text-xs text-text-body"
        >
          Ya, saya juga ingin mendapatkan komunikasi pemasaran dan promosi SPH
          Media Group.
          <div className="relative group inline-block">
            {/* Info icon */}
            <img
              src={infoIcon}
              alt="info"
              width={12}
              className="cursor-pointer"
            />

            {/* Tooltip Desktop*/}
            <div
              className={`${styles.tooltipBg} absolute z-10 -top-12 lg:top-1/2 lg:left-full -translate-y-1/2 rounded-md lg:ml-1
                w-[192px] h-[97.5px] lg:w-[210px] lg:h-[92px] px-2 pt-2 pb-[19.5px] lg:py-[6px] lg:pl-[18px] lg:pr-[6px]
                invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-300`}
            >
              <div className="w-[176px] lg:w-[186px] text-text-body text-xxs lg:text-[13px] leading-[150%]">
                SPH Media Limited, perbadanan dan sekutunya yang berkaitan serta
                ejen mereka dan pembekal perkhidmatan yang diberi kuasa.
              </div>
            </div>
          </div>
        </label>
      </div>
      <button
        className={twMerge(
          styles.btnNewsletter,
          "text-xxs w-full lg:w-[218px] h-[35px]",
        )}
        aria-label="Sign Up for Newsletter"
        type="submit"
      >
        Langgan
      </button>
    </form>
  );
}
